import React, { useRef, useState } from 'react';
import { Table, Segment, Icon, Button } from 'semantic-ui-react';
import './Chart.css';
import './styles/DashCharts.scss';

interface TableRow {
    [key: string]: string | number;
}

interface DashTableProps {
    data: TableRow[];
    title: string;
}

const DashTable: React.FC<DashTableProps> = (props) => {
    // State for table functionality (not data fetching)
    const [filteredData, setFilteredData] = useState<TableRow[] | null>(props.data);
    const [sortState, setSortState] = useState<Record<string, 'asc' | 'desc' | null>>({});
    const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(null);
    const [filterState, setFilterState] = useState<Record<string, Set<string>>>({});
    const filterRef = useRef<HTMLDivElement | null>(null);

    // Handle no data case
    if (!props.data || !Array.isArray(props.data) || props.data.length === 0) {
        return (
            <Segment>
                <p>No data available</p>
            </Segment>
        );
    }

    // Initialize states if they haven't been yet
    if (!filteredData && props.data && Array.isArray(props.data) && props.data.length > 0) {
        setFilteredData(props.data);

        if (props.data[0]) {
            // Initialize sort state
            const initialSortState: Record<string, 'asc' | 'desc' | null> = {};
            Object.keys(props.data[0]).forEach(column => {
                initialSortState[column] = null;
            });
            setSortState(initialSortState);

            // Initialize filter state
            const initialFilterState: Record<string, Set<string>> = {};
            Object.keys(props.data[0]).forEach(column => {
                initialFilterState[column] = new Set(props.data.map(row => String(row[column])));
            });
            setFilterState(initialFilterState);
        }
    }

    // Handle sorting
    const handleSort = (column: string) => {
        const currentSort = sortState[column];
        const newSort: 'asc' | 'desc' | null = currentSort === 'asc' ? 'desc' : currentSort === 'desc' ? null : 'asc';

        setSortState(prev => ({ ...prev, [column]: newSort }));

        if (filteredData) {
            const sortedData = [...filteredData].sort((a, b) => {
                if (newSort === 'asc') return String(a[column]) > String(b[column]) ? 1 : -1;
                if (newSort === 'desc') return String(a[column]) < String(b[column]) ? 1 : -1;
                return 0;
            });
            setFilteredData(newSort ? sortedData : props.data);
        }
    };

    // Handle filtering
    const handleCheckboxChange = (column: string, value: string) => {
        setFilterState((prev) => {
            const updatedSet = new Set(prev[column]);
            if (updatedSet.has(value)) {
                updatedSet.delete(value);
            } else {
                updatedSet.add(value);
            }

            const updatedFilterState = { ...prev, [column]: updatedSet };

            // Apply the filters dynamically
            const newFilteredData = props.data.filter((row) =>
                Object.keys(updatedFilterState).every((col) =>
                    updatedFilterState[col].size === 0 || updatedFilterState[col].has(String(row[col]))
                )
            );

            setFilteredData(newFilteredData);
            return updatedFilterState;
        });
    };

    // Render filter dropdown
    const renderFilterDropdown = (column: string) => {
        const distinctValues = Array.from(new Set(props.data.map((row) => String(row[column]))));
        const isChecked = (value: string) => filterState[column]?.has(value) || false;

        return (
            <div
                ref={filterRef}
                style={{
                    position: 'absolute',
                    background: 'white',
                    zIndex: 10,
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '5px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    maxHeight: '200px',
                    overflowY: 'auto',
                }}
            >
                { distinctValues.map((value) => (
                    <label
                        key={value}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '5px',
                            cursor: 'pointer',
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={isChecked(value)}
                            onChange={() => handleCheckboxChange(column, value)}
                            style={{ marginRight: '5px' }}
                        />
                        {value}
                    </label>
                ))}
                <Button size="mini" onClick={() => setActiveFilterColumn(null)} style={{ marginTop: '10px' }}>
                    Close
                </Button>
            </div>
        );
    };

    // Get column names from the data
    const columnNames = Object.keys(props.data[0]);
    const dataToDisplay = filteredData || props.data;

    // Ensure dataToDisplay is an array before rendering
    if (!Array.isArray(dataToDisplay)) {
        return (
            <Segment>
                <p>Invalid data format</p>
            </Segment>
        );
    }

    return (
        <div style={{ height: '100%', width: '100%', overflow: 'auto', boxSizing: 'border-box' }}>
        <Table celled striped style={{ borderRadius: '15px', overflow: 'hidden', borderCollapse: 'separate', borderSpacing: 0, marginTop: '10px', width: '100%' }}>
            <Table.Header>
                <Table.Row>
                    {columnNames.map((column) => (
                        <Table.HeaderCell key={column}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                {column}
                                <div>
                                    <Icon
                                        name={sortState[column] === 'asc' ? 'sort up' : sortState[column] === 'desc' ? 'sort down' : 'sort'}
                                        onClick={() => handleSort(column)}
                                        style={{ cursor: 'pointer', marginLeft: '8px' }}
                                    />
                                    <Icon
                                        name="filter"
                                        onClick={() => setActiveFilterColumn(column)}
                                        style={{ cursor: 'pointer', marginLeft: '8px' }}
                                    />
                                </div>
                            </div>
                            {activeFilterColumn === column && renderFilterDropdown(column)}
                        </Table.HeaderCell>
                    ))}
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {dataToDisplay.map((row: TableRow, rowIndex) => (
                    <Table.Row key={rowIndex}>
                        {columnNames.map((column) => (
                            <Table.Cell key={column}>{String(row[column])}</Table.Cell>
                        ))}
                    </Table.Row>
                ))}
            </Table.Body>
        </Table>
        </div>
    );
};

export default DashTable;
